import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User, Mail, CreditCard, MapPin, Briefcase, Hash, Building, Home,
  ChevronRight, Phone, Calendar, Flag, FileText, Users, Layers,
  Tag, CheckSquare, CircleHelp, ImagePlus, Loader2
} from 'lucide-react';
import { Socio } from '../types/Socio';
import { useAuth0 } from '@auth0/auth0-react';
import socioService from '../services/socioService.tsx';
import completSocio from '../utils/socioUtils.tsx';

const initialSocio: Socio = {
  socio_mail: '',
  socio_dni: '',
  socio_cuit: '',
  socio_domicilio: '',
  socio_id: '',
  socio_nombre: '',
  socio_tipoEmpresa: '',
  socio_campanias: '',
  socio_acta: 0,
  socio_celular: '',
  socio_condicionFiscal: 0,
  socio_enviarMail: false,
  socio_estado: 0,
  socio_fechaNacimiento: '',
  socio_firma: '',
  socio_gestion: '',
  socio_habilitacion: '',
  socio_localidad: 0,
  socio_modificado: '',
  socio_motivoBaja: '',
  socio_nacionalidad: '',
  socio_numero: 0,
  socio_otroTelefono: '',

  socio_rubro: 0,
  socio_sector: 0,
  socio_segmento: '',
  socio_telefono: '',
  socio_tieneCajaSeguridad: false,
  socio_tipoSocio: "",
  logoUrl: "",
};

const iconMap = {
  socio_numero: Hash,
  socio_nombre: User,
  socio_mail: Mail,
  socio_dni: CreditCard,
  socio_cuit: Hash,
  socio_domicilio: MapPin,
  socio_id: Briefcase,
  socio_tipoEmpresa: Building,
  socio_campanias: FileText,
  socio_acta: FileText,
  socio_celular: Phone,
  socio_condicionFiscal: FileText,
  socio_enviarMail: Mail,
  socio_estado: FileText,
  socio_fechaNacimiento: Calendar,
  socio_firma: FileText,
  socio_gestion: FileText,
  socio_habilitacion: FileText,
  socio_localidad: MapPin,
  socio_modificado: Calendar,
  socio_motivoBaja: FileText,
  socio_nacionalidad: Flag,
  socio_otroTelefono: Phone,
  socio_padrino1: Users,
  socio_padrino2: Users,
  socio_rubro: Layers,
  socio_sector: Layers,
  socio_segmento: Tag,
  socio_telefono: Phone,
  socio_tieneCajaSeguridad: CheckSquare,
  socio_tipoSocio: User,
  logoUrl: FileText,
};

const labelMap: { [key: string]: string } = {
  socio_fechaNacimiento: 'Fecha de Nacimiento',
  socio_numero: 'Número',
  socio_nombre: 'Nombre',
  socio_mail: 'Domicilio electrónico',
  socio_dni: 'DNI',
  socio_cuit: 'CUIT',
  socio_domicilio: 'Domicilio',
  socio_id: 'Socio ID',
  socio_tipoEmpresa: 'Tipo de Empresa',
  socio_campanias: 'Campañas',
  socio_acta: 'Acta',
  socio_celular: 'Celular',
  socio_condicionFiscal: 'Condición Fiscal',
  socio_enviarMail: 'Enviar Mail',
  socio_estado: 'Estado',
  socio_firma: 'Firma',
  socio_gestion: 'Gestión',
  socio_habilitacion: 'Habilitación',
  socio_localidad: 'Localidad',
  socio_modificado: 'Modificado',
  socio_motivoBaja: 'Motivo de Baja',
  socio_nacionalidad: 'Nacionalidad',
  socio_otroTelefono: 'Otro Teléfono',
  socio_padrino1: 'Padrino 1',
  socio_padrino2: 'Padrino 2',
  socio_rubro: 'Rubro',
  socio_sector: 'Sector',
  socio_segmento: 'Segmento',
  socio_telefono: 'Teléfono',
  socio_tieneCajaSeguridad: 'Tiene Caja Seguridad',
  socio_tipoSocio: 'Tipo de Socio',
  logoUrl: 'Logo URL',
};

const CONDICION_FISCAL_MAP: Record<number, string> = {
  1: 'Responsable Inscripto',
  2: 'Monotributista',
  3: 'Exento',
  4: 'Consumidor Final',
  5: 'No Responsable',
};

const formatFieldValue = (key: string, value: any) => {
  if (key === 'socio_condicionFiscal') {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 'No informado';
    return CONDICION_FISCAL_MAP[n] ? `${CONDICION_FISCAL_MAP[n]} (${n})` : `Código ${n}`;
  }

  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return 'N/A';
  return value || 'N/A';
};

const ProfileField: React.FC<{ label: string; value: any; icon: React.ElementType; fieldKey: string }> = ({ label, value, icon: Icon, fieldKey }) => (
  <li className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white/70 px-3 py-2 shadow-sm">
    <div className="mt-1 rounded-lg bg-slate-100 p-2 text-slate-600">
      <Icon className="h-4 w-4" />
    </div>
    <div className="flex flex-col">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-sm text-slate-800">{formatFieldValue(fieldKey, value)}</span>
    </div>
  </li>
);

const DOMICILIO_TOOLTIP =
  "Es el medio por el cual se le va a informar de cualquier cosa importante.";

const DomicilioElectronicoBadge: React.FC<{ value: string }> = ({ value }) => (
  <span className="group mt-1 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">
    Domicilio electrónico: {value}
    <span className="relative inline-flex items-center">
      <CircleHelp className="h-3.5 w-3.5 cursor-help text-blue-600" />
      <span className="pointer-events-none absolute left-1/2 top-[calc(100%+0.4rem)] z-20 w-64 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-center text-[11px] normal-case tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {DOMICILIO_TOOLTIP}
      </span>
    </span>
  </span>
);

export default function UserProfile() {
  const [socio, setSocio] = useState<Socio>(initialSocio);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoMessage, setLogoMessage] = useState<string | null>(null);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const cachedSocio = localStorage.getItem('socioSeleccionado');

        if (cachedSocio) {
          const socioData: Socio = JSON.parse(cachedSocio);
          const socio = await socioService.getSocioById(socioData.socio_id, getAccessTokenSilently);
          setSocio({
            ...socio,
            socio_firma: socio.socio_firma || socio.logoUrl || "",
          });
        } else {
          console.error('No se encontró un socio seleccionado en el localStorage.');
          setError('No se encontró un socio seleccionado en el localStorage.');
          navigate('/seleccionar-socio');
        }
      } catch (error) {
        setError('Error al cargar los datos del usuario');
        console.error('Error al obtener el perfil de usuario:', error);
      } finally { 
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate, getAccessTokenSilently]);

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const camposExcluidos = [
    'socio_id',
    'socio_modificado',
    'socio_firma',
    'socio_localidad_nombre',
    'logoUrl',
    'socio_logo',
    'socio_logoComercio',
    'socio_deleted',
    'socio_estado',
    'socio_fechaNacimiento',
    'socio_habilitacion',
    'socio_tarjetaFechaEntrega',
    'socio_tarjetaEntregada',
    'socio_enviarMail',
    'socio_apellido',
    'socio_padrino1',
    'socio_padrino2',
    'socio_acta',
    'socio_motivoBaja',
    'socio_Campanias',
    'pagos_locales',
    'sos_sync_status',

    // otros campos que quieras ocultar
  ];
  
  const SocioEntries = Object.entries(completSocio(socio)).filter(([key, value]) => {
    const normalizedKey = key.toLowerCase();
    if (camposExcluidos.includes(key)) return false;
    if (normalizedKey.includes("logo")) return false;
    if (Array.isArray(value)) return false;
    if (typeof value === "object" && value !== null) return false;
    return true;
  });
  const midpoint = Math.ceil(SocioEntries.length / 2);
  const domicilioElectronico = socio?.socio_mail || "No informado";
  const logoActual = logoPreview || socio?.logoUrl || socio?.socio_firma || "";
  const canRenderLogo = Boolean(logoActual) && !logoLoadError;

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      setLogoMessage("Seleccioná una imagen válida (PNG/JPG/WebP).");
      return;
    }

    if (logoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoMessage(null);
    setLogoLoadError(false);
    setLogoFile(selected);
    setLogoPreview(URL.createObjectURL(selected));
  };

  const handleActualizarLogo = async () => {
    if (!logoFile || !socio?.socio_id) return;
    try {
      setIsUploadingLogo(true);
      setLogoMessage(null);
      const logoUrl = await socioService.uploadSocioLogo(
        String(socio.socio_id),
        logoFile,
        getAccessTokenSilently
      );

      if (logoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }

      const logoUrlFresh = `${logoUrl}${logoUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;

      setLogoPreview(logoUrlFresh);
      setLogoLoadError(false);
      setLogoFile(null);
      setSocio((prev) => ({
        ...prev,
        logoUrl: logoUrlFresh,
        socio_firma: logoUrlFresh,
      }));

      const cached = localStorage.getItem("socioSeleccionado");
      if (cached) {
        const socioCached = JSON.parse(cached);
        localStorage.setItem(
          "socioSeleccionado",
          JSON.stringify({
            ...socioCached,
            logoUrl: logoUrlFresh,
            socio_firma: logoUrlFresh,
          })
        );
      }

      setLogoMessage("Logo actualizado correctamente.");
    } catch (e) {
      console.error("No se pudo actualizar el logo del socio:", e);
      setLogoMessage("No se pudo actualizar el logo. Reintentá.");
    } finally {
      setIsUploadingLogo(false);
    }
  };

return (
  <div className="container mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
    {/* Breadcrumb Navigation */}
    <nav className="flex mb-4" aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 md:space-x-3">
        <li className="inline-flex items-center">
          <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
            <Home className="w-4 h-4 mr-2" />
            Inicio
          </Link>
        </li>
        <li>
          <div className="flex items-center">
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Perfil</span>
          </div>
        </li>
      </ol>
    </nav>

    <div className="w-full rounded-3xl bg-white p-4 sm:p-6 md:p-8 shadow-xl">
      {error && (
        <div className="mb-4 rounded-lg bg-red-100 px-4 py-3 text-red-700">{error}</div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-lg font-bold text-white shadow-lg">
            {socio?.socio_nombre ? socio.socio_nombre.split(" ").slice(0, 2).map((n) => n[0]).join("") : "S"}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Perfil de Usuario
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              {socio?.socio_nombre || "Socio"}
            </h1>
            <DomicilioElectronicoBadge value={domicilioElectronico} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/pagos"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <CreditCard className="h-4 w-4" />
            Ir a pagos
          </Link>
          <Link
            to="/editar-perfil"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Editar Perfil
          </Link>
        </div>
      </div>

      {/* Quick summary */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Socio ID" value={socio?.socio_id || "—"} />
        <SummaryCard label="CUIT" value={socio?.socio_cuit || "—"} />
        <SummaryCard label="DNI" value={socio?.socio_dni || "—"} />
        <SummaryCard
          label="Caja de seguridad"
          value={socio?.socio_tieneCajaSeguridad ? "Sí" : "No"}
          tone={socio?.socio_tieneCajaSeguridad ? "emerald" : "slate"}
        />
      </div>

      <div className="mt-6 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Logo del comercio</p>
            <h3 className="text-lg font-semibold text-slate-900">Imagen principal del socio</h3>
            <p className="text-sm text-slate-600">Podés reemplazarla en cualquier momento.</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ImagePlus className="h-4 w-4" />
              Elegir logo
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoFileChange}
                className="hidden"
              />
            </label>
            <button
              onClick={handleActualizarLogo}
              disabled={!logoFile || isUploadingLogo}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {isUploadingLogo && <Loader2 className="h-4 w-4 animate-spin" />}
              {isUploadingLogo ? "Actualizando..." : "Modificar logo"}
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-blue-300 bg-white p-4">
          {canRenderLogo ? (
            <img
              src={logoActual}
              alt="Logo del socio"
              onError={() => {
                setLogoLoadError(true);
                setLogoMessage("No se pudo cargar el logo desde storage. Revisá URL/container.");
              }}
              className="h-44 w-full max-w-[20rem] rounded-lg border border-slate-200 bg-white object-contain"
            />
          ) : (
            <div className="flex h-44 w-full max-w-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                <User className="h-8 w-8" />
              </div>
              <span>{logoActual ? "Logo no disponible" : "El socio no tiene logo cargado"}</span>
            </div>
          )}
          {logoMessage && (
            <p className="mt-3 text-sm font-medium text-slate-700">{logoMessage}</p>
          )}
        </div>
      </div>

      {/* Information Cards */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        {/* Personal Information */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-4 sm:p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <User className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-blue-700">Información Personal</h2>
          </div>
          {isLoading ? (
            <SkeletonList count={midpoint} />
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {SocioEntries.slice(0, midpoint).map(([key, value]) => (
                <ProfileField
                  key={key}
                  fieldKey={key}
                  label={labelMap[key] || key}
                  value={value}
                  icon={iconMap[key as keyof typeof iconMap] || FileText}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Company Information */}
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-white p-4 sm:p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <Briefcase className="h-6 w-6 text-emerald-600" />
            <h2 className="text-xl font-semibold text-emerald-700">Información Empresarial</h2>
          </div>
          {isLoading ? (
            <SkeletonList count={midpoint} />
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {SocioEntries.slice(midpoint).map(([key, value]) => (
                <ProfileField
                  key={key}
                  fieldKey={key}
                  label={labelMap[key] || key}
                  value={value}
                  icon={iconMap[key as keyof typeof iconMap] || FileText}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  </div>
);

}

const SkeletonList = ({ count }) => (
  <ul className="grid gap-3 md:grid-cols-2">
    {Array.from({ length: count }).map((_, index) => (
      <li key={index} className="h-12 rounded-lg bg-slate-200/70 animate-pulse" />
    ))}
  </ul>
);
const SummaryCard = ({
  label,
  value,
  tone = "slate",
}: {
  label: string;
  value: string | number;
  tone?: "emerald" | "slate";
}) => {
  const toneClasses =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`rounded-xl border ${toneClasses} p-3 shadow-sm`}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
};
